import { Tooltip } from "@carbon6/c6-frontend-design";
import type { TrainingBudgetUnit } from "@/api";
import { formatCompactCount, formatNumber } from "@/utils/formats";

const EMPTY_CELL = "—";

// 평가 표의 `#`(수행 횟수) + `UNIT`(그 횟수의 단위) 한 쌍을 그리는 규칙. Policy 카탈로그와
// Evaluation Results 표가 같은 계약을 공유한다. 그래서 축약·라벨·hover·정렬 판정을 이 파일에 모은다.

export function budgetUnitCellLabel(unit: TrainingBudgetUnit): string {
  return unit === "step" ? "Step" : "Epoch";
}

// `#` 열에는 epoch 행과 step 행이 섞일 수 있다. 그래서 표에 그린 행의 단위가 하나일 때만
// `#` 으로 정렬할 수 있다. 이미 `#` 으로 정렬 중이면 단위가 섞여도 정렬을 끄지 않는다.
// sort state 는 DataTable 이 소유한다. 헤더 버튼이 사라지면 정렬을 되돌릴 방법이 없다.
export function isCheckpointSortable(
  rows: readonly { readonly budgetUnit: TrainingBudgetUnit }[],
  sortedByCheckpoint: boolean,
): boolean {
  if (sortedByCheckpoint) {
    return true;
  }
  const units = new Set(rows.map((row) => row.budgetUnit));
  return units.size <= 1;
}

// `#` 셀 텍스트. step 카운트만 K/M/B 로 축약한다. epoch 은 자릿수가 작고, 정확한 값이
// checkpoint 를 식별한다. 그래서 epoch 은 그대로 둔다. 축약으로 잃은 전체값은 셀 hover 가 표시한다.
export function checkpointCellText(value: number | null, unit: TrainingBudgetUnit): string {
  if (value === null) {
    return EMPTY_CELL;
  }
  return unit === "step" ? formatCompactCount(value) : String(value);
}

// `#` 셀. hover 는 축약으로 잃은 전체값과 세션이 학습한 총량을 단위에 맞춰 표시한다.
export function CheckpointCount({
  value,
  unit,
  sessionTotalEpochs,
  sessionTotalSteps,
}: {
  value: number | null;
  unit: TrainingBudgetUnit;
  sessionTotalEpochs: number | null;
  sessionTotalSteps: number | null;
}) {
  const text = checkpointCellText(value, unit);
  if (value === null) {
    return <span>{text}</span>;
  }
  const sessionTotal = unit === "step" ? sessionTotalSteps : sessionTotalEpochs;
  const notes: string[] = [];
  if (unit === "step") {
    notes.push(`Step ${formatNumber(value)}.`);
  }
  if (sessionTotal !== null) {
    notes.push(`Trained for ${formatNumber(sessionTotal)} ${unit}s in total.`);
  }
  // 이 툴팁은 셀 텍스트와 다른 내용을 표시한다. 그래서 잘림 마커가 아니라 저작 툴팁이다.
  // 표시할 문구가 없으면 content 가 비어 있다. 그때 c6 는 래퍼를 제거하고 span 만 남긴다.
  return (
    <Tooltip content={notes.length > 0 ? notes.join(" ") : undefined}>
      <span>{text}</span>
    </Tooltip>
  );
}
